import React, { useState, useEffect } from "react";
import Select from "react-select";
import { URL, getToken } from "../utils";
import { customSelectStyles } from "../utils/selectStyles";
import { useCountries } from "../hooks/useCountries";
import axios from "axios";
import { toast } from "react-toastify";

// Secteurs côté professionnels (valeurs identiques au formulaire client pro)
const SECTEUR_OPTIONS_PRO = [
  { value: "shop", label: "Boutique" },
  { value: "bar", label: "Bar" },
  { value: "restaurant", label: "Restaurant" },
  { value: "hôtel", label: "Hôtel" },
  { value: "Company", label: "Compagnie" },
  { value: "night_etablissement", label: "Établissement de nuit" },
];

// Secteurs côté prospects (valeurs identiques au formulaire prospect)
const SECTEUR_OPTIONS_PROSPECT = [
  { value: "shop", label: "Boutique" },
  { value: "bar", label: "Bar" },
  { value: "restaurant", label: "Restaurant" },
  { value: "hotel", label: "Hôtel" },
  { value: "Company", label: "Entreprise" },
  { value: "night_etablishement", label: "Établissement de nuit" },
];

// Champs à envoyer en nombre entier
const INTEGER_FIELDS = [
  "magasin",
  "commercial",
  "commercial_id",
  "produit_deguste",
  "stock_item",
  "age_min",
  "age_max",
];

// Champs à envoyer en nombre décimal
const NUMBER_FIELDS = [
  "ca_min",
  "ca_max",
  "prix_total_min_achat",
  "prix_total_max_achat",
];

// Styles react-select avec menu en portail (évite le rognage dans le modal)
const reactSelectStyles = {
  ...customSelectStyles,
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const FilterModal = ({
  isOpen,
  onClose,
  onApplyFilters,
  filterType = "particuliers", // "particuliers", "professionnels" ou "prospects"
  title = "Filtrer les clients",
}) => {
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(false);
  const [magasins, setMagasins] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [produits, setProduits] = useState([]);
  const { countries } = useCountries();

  const token = getToken();

  // Structure des filtres selon le type de client
  const getInitialFilters = () => {
    if (filterType === "particuliers") {
      return {
        age_min: "",
        age_max: "",
        nationalites: [],
        adresses: [],
        ca_min: "",
        ca_max: "",
        sexe: "",
        magasin: "",
        stock_item: "",
        produit_deguste: "",
        date_debut: "",
        date_fin: "",
        commercial: "",
        event: [],
        is_father: undefined,
        is_mother: undefined,
        is_grand_father: undefined,
        is_grand_mother: undefined,
        at_event: undefined,
        jour_naissance: "",
        mois_naissance: "",
      };
    } else if (filterType === "professionnels") {
      return {
        secteur_activites: [],
        stock_item: "",
        ca_min: "",
        ca_max: "",
        pays: [],
        date_debut: "",
        date_fin: "",
        commercial: "",
        adresse: [],
        prix_total_min_achat: "",
        prix_total_max_achat: "",
        is_in_debt: "",
      };
    } else {
      // prospects - champs conformes à l'API Prospects/prospects-filter
      return {
        is_qualified: "",
        has_rendez_vous: "",
        date_rendez_vous_start: "",
        date_rendez_vous_end: "",
        secteurs_activite: [],
        professions: [],
        nationalites: [],
        produit_deguste: "",
        magasin: "",
        commercial_id: "",
        date_debut: "",
        date_fin: "",
        is_active: "",
        is_professionnel: "",
        sexe: "",
      };
    }
  };

  // Initialise (ou réinitialise) les filtres uniquement quand le type change,
  // pas à chaque ouverture : les valeurs saisies sont ainsi conservées tant
  // que l'utilisateur n'a pas cliqué sur « Réinitialiser ».
  useEffect(() => {
    setFilters(getInitialFilters());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  // Charge les données de référence à l'ouverture, sans toucher aux filtres.
  useEffect(() => {
    if (isOpen) {
      fetchReferenceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, filterType]);

  const fetchReferenceData = async () => {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      // Récupérer les données de référence
      const [magasinsRes, commerciauxRes, produitsRes] = await Promise.all([
        axios.get(URL + "Magasins/magasins-list", { headers }),
        axios.get(URL + "Magasins/commercial/liste-commerciaux", { headers }),
        axios
          .get(URL + "Magasins/liste-all-products", { headers })
          .catch(() => ({ data: [] })),
      ]);

      setMagasins(magasinsRes.data || []);
      setCommerciaux(commerciauxRes.data || []);
      setProduits(produitsRes.data.stock_items || []);
    } catch (error) {
      console.error(
        "Erreur lors du chargement des données de référence:",
        error,
      );
      toast.error("Erreur lors du chargement des données de référence");
    }
  };

  const handleInputChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Construit le payload : combine jour/mois de naissance, supprime les
  // valeurs vides et convertit les champs numériques. Les booléens (true
  // ET false) sont conservés pour les selects à trois états.
  const buildPayload = ({ excel = false } = {}) => {
    const source = { ...filters };

    // jour_naissance attendu au format MM-DD (nécessite le mois)
    if (source.jour_naissance && source.mois_naissance) {
      source.jour_naissance = `${source.mois_naissance}-${String(
        source.jour_naissance,
      ).padStart(2, "0")}`;
    } else {
      delete source.jour_naissance;
    }

    const payload = Object.entries(source).reduce((acc, [key, value]) => {
      if (key === "export_excel") return acc;
      if (
        value === "" ||
        value === null ||
        value === undefined ||
        (Array.isArray(value) && value.length === 0)
      ) {
        return acc;
      }
      if (INTEGER_FIELDS.includes(key)) {
        const parsed = parseInt(value, 10);
        if (!Number.isNaN(parsed)) acc[key] = parsed;
      } else if (NUMBER_FIELDS.includes(key)) {
        const parsed = Number(value);
        if (!Number.isNaN(parsed)) acc[key] = parsed;
      } else {
        acc[key] = value;
      }
      return acc;
    }, {});

    if (excel) payload.export_excel = true;
    return payload;
  };

  const getEndpoint = () =>
    filterType === "particuliers"
      ? "Clients/clients-particuliers/filtrer"
      : filterType === "professionnels"
        ? "Clients/clients-professionnels/filtrer"
        : "Prospects/prospects-filter";

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const cleanedFilters = buildPayload();

      const response = await axios.post(URL + getEndpoint(), cleanedFilters, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = response.data;
      let results;
      if (Array.isArray(data)) {
        results = data;
      } else if (Array.isArray(data?.data)) {
        results = data.data;
      } else if (Array.isArray(data?.results)) {
        results = data.results;
      } else if (data && typeof data === "object") {
        // Repli : certains endpoints renvoient la liste sous une clé
        // spécifique (ex. "response"). On prend le premier tableau trouvé
        // dans l'objet de réponse.
        results = Object.values(data).find((v) => Array.isArray(v)) ?? [];
      } else {
        results = [];
      }

      onApplyFilters(results);
      onClose();
      toast.success("Filtres appliqués avec succès");
    } catch (error) {
      console.error("Erreur lors de l'application des filtres:", error);
      toast.error("Erreur lors de l'application des filtres");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFilters(getInitialFilters());
  };

  const handleExportExcel = async () => {
    setLoading(true);
    try {
      const cleanedFilters = buildPayload({ excel: true });

      const response = await axios.post(URL + getEndpoint(), cleanedFilters, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        responseType: "blob",
      });

      // Télécharger le fichier Excel
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `clients_${filterType}_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Export Excel téléchargé avec succès");
    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      toast.error("Erreur lors de l'export Excel");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Filtres communs */}
            {filterType !== "prospects" && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Chiffre d'affaire minimum (FCFA)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.ca_min || ""}
                    onChange={(e) =>
                      handleInputChange("ca_min", e.target.value)
                    }
                    placeholder="0.00"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Chiffre d'affaire maximum (FCFA)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.ca_max || ""}
                    onChange={(e) =>
                      handleInputChange("ca_max", e.target.value)
                    }
                    placeholder="999999.99"
                  />
                </div>
              </>
            )}

            {filterType !== "professionnels" && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Magasin
                </label>
                <select
                  className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                  value={filters.magasin || ""}
                  onChange={(e) => handleInputChange("magasin", e.target.value)}
                >
                  <option value="">Tous les magasins</option>
                  {magasins.map((magasin) => (
                    <option key={magasin.id} value={magasin.id}>
                      {magasin.nom || magasin.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Commercial
              </label>
              <select
                className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                value={
                  filterType === "prospects"
                    ? filters.commercial_id || ""
                    : filters.commercial || ""
                }
                onChange={(e) =>
                  handleInputChange(
                    filterType === "prospects" ? "commercial_id" : "commercial",
                    e.target.value,
                  )
                }
              >
                <option value="">Tous les commerciaux</option>
                {commerciaux.map((commercial) => (
                  <option key={commercial.id} value={commercial.id}>
                    {commercial.nom} {commercial.prenom}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Date de début
              </label>
              <input
                type="date"
                className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                value={filters.date_debut || ""}
                onChange={(e) =>
                  handleInputChange("date_debut", e.target.value)
                }
              />
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Date de fin
              </label>
              <input
                type="date"
                className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                value={filters.date_fin || ""}
                onChange={(e) => handleInputChange("date_fin", e.target.value)}
              />
            </div>

            {/* Champ produit dégusté (pour particuliers et prospects uniquement) */}
            {filterType !== "professionnels" && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Produit dégusté
                </label>
                <select
                  className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                  value={filters.produit_deguste || ""}
                  onChange={(e) =>
                    handleInputChange("produit_deguste", e.target.value)
                  }
                >
                  <option value="">Tous les produits</option>
                  {produits.map((produit) => (
                    <option key={produit.id} value={produit.id}>
                      {produit.nom || produit.name || produit.product_name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Filtres spécifiques aux particuliers */}
            {filterType === "particuliers" && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Âge minimum
                  </label>
                  <input
                    type="number"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.age_min || ""}
                    onChange={(e) =>
                      handleInputChange("age_min", e.target.value)
                    }
                    placeholder="18"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Âge maximum
                  </label>
                  <input
                    type="number"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.age_max || ""}
                    onChange={(e) =>
                      handleInputChange("age_max", e.target.value)
                    }
                    placeholder="99"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sexe
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.sexe || ""}
                    onChange={(e) => handleInputChange("sexe", e.target.value)}
                  >
                    <option value="">Tous</option>
                    <option value="M">Masculin</option>
                    <option value="F">Féminin</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Mois de naissance
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.mois_naissance || ""}
                    onChange={(e) =>
                      handleInputChange("mois_naissance", e.target.value)
                    }
                  >
                    <option value="">Tous les mois</option>
                    {Array.from({ length: 12 }, (_, i) => {
                      const month = String(i + 1).padStart(2, "0");
                      const monthName = new Date(2000, i, 1).toLocaleString(
                        "fr-FR",
                        { month: "long" },
                      );
                      return (
                        <option key={month} value={month}>
                          {monthName}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Jour de naissance
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.jour_naissance || ""}
                    onChange={(e) =>
                      handleInputChange("jour_naissance", e.target.value)
                    }
                  >
                    <option value="">Tous les jours</option>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = String(i + 1).padStart(2, "0");
                      return (
                        <option key={day} value={day}>
                          {i + 1}
                        </option>
                      );
                    })}
                  </select>
                  <p className="text-xs text-gray-500">
                    Combiné avec le mois sélectionné
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Article acheté
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.stock_item || ""}
                    onChange={(e) =>
                      handleInputChange("stock_item", e.target.value)
                    }
                  >
                    <option value="">Tous les articles</option>
                    {produits.map((produit) => (
                      <option key={produit.id} value={produit.id}>
                        {produit.nom || produit.name || produit.product_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Nationalités
                  </label>
                  <Select
                    isMulti
                    options={countries}
                    value={countries.filter((c) =>
                      (filters.nationalites || []).includes(c.value),
                    )}
                    onChange={(selected) =>
                      handleInputChange(
                        "nationalites",
                        selected ? selected.map((s) => s.value) : [],
                      )
                    }
                    placeholder="Sélectionner..."
                    noOptionsMessage={() => "Aucun résultat"}
                    styles={reactSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Adresses
                  </label>
                  <input
                    type="text"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.adresses?.join(", ") || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "adresses",
                        e.target.value
                          ? e.target.value.split(",").map((s) => s.trim())
                          : [],
                      )
                    }
                    placeholder="Ex: Lomé, Adidogomé"
                  />
                  <p className="text-xs text-gray-500">
                    Séparez par des virgules
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Événements
                  </label>
                  <input
                    type="text"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.event?.join(", ") || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "event",
                        e.target.value
                          ? e.target.value.split(",").map((s) => s.trim())
                          : [],
                      )
                    }
                    placeholder="Ex: Mariage, Anniversaire"
                  />
                  <p className="text-xs text-gray-500">
                    Séparez par des virgules
                  </p>
                </div>

                {/* Cases à cocher pour les statuts familiaux */}
                <div className="col-span-full">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut familial
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { key: "is_father", label: "Père" },
                      { key: "is_mother", label: "Mère" },
                      { key: "is_grand_father", label: "Grand-père" },
                      { key: "is_grand_mother", label: "Grand-mère" },
                    ].map(({ key, label }) => (
                      <label key={key} className="flex items-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                          checked={!!filters[key]}
                          onChange={(e) =>
                            handleInputChange(
                              key,
                              e.target.checked || undefined,
                            )
                          }
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>

                  <label className="flex items-center mt-4">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-amber-500 focus:ring-amber-500"
                      checked={!!filters.at_event}
                      onChange={(e) =>
                        handleInputChange(
                          "at_event",
                          e.target.checked || undefined,
                        )
                      }
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      A assisté à un événement
                    </span>
                  </label>
                </div>
              </>
            )}

            {/* Filtres spécifiques aux professionnels */}
            {filterType === "professionnels" && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prix total minimum des achats
                  </label>
                  <input
                    type="number"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.prix_total_min_achat || ""}
                    onChange={(e) =>
                      handleInputChange("prix_total_min_achat", e.target.value)
                    }
                    placeholder="0"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Prix total maximum des achats
                  </label>
                  <input
                    type="number"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.prix_total_max_achat || ""}
                    onChange={(e) =>
                      handleInputChange("prix_total_max_achat", e.target.value)
                    }
                    placeholder="999999"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Secteurs d'activité
                  </label>
                  <Select
                    isMulti
                    options={SECTEUR_OPTIONS_PRO}
                    value={SECTEUR_OPTIONS_PRO.filter((o) =>
                      (filters.secteur_activites || []).includes(o.value),
                    )}
                    onChange={(selected) =>
                      handleInputChange(
                        "secteur_activites",
                        selected ? selected.map((s) => s.value) : [],
                      )
                    }
                    placeholder="Sélectionner..."
                    noOptionsMessage={() => "Aucun résultat"}
                    styles={reactSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Pays
                  </label>
                  <Select
                    isMulti
                    options={countries}
                    value={countries.filter((c) =>
                      (filters.pays || []).includes(c.value),
                    )}
                    onChange={(selected) =>
                      handleInputChange(
                        "pays",
                        selected ? selected.map((s) => s.value) : [],
                      )
                    }
                    placeholder="Sélectionner..."
                    noOptionsMessage={() => "Aucun résultat"}
                    styles={reactSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Article acheté
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.stock_item || ""}
                    onChange={(e) =>
                      handleInputChange("stock_item", e.target.value)
                    }
                  >
                    <option value="">Tous les articles</option>
                    {produits.map((produit) => (
                      <option key={produit.id} value={produit.id}>
                        {produit.nom || produit.name || produit.product_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Débiteur
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={
                      filters.is_in_debt === ""
                        ? ""
                        : filters.is_in_debt === true
                          ? "true"
                          : "false"
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "is_in_debt",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">Tous</option>
                    <option value="true">Débiteurs</option>
                    <option value="false">Non débiteurs</option>
                  </select>
                </div>

                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Adresses de facturation
                  </label>
                  <input
                    type="text"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.adresse?.join(", ") || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "adresse",
                        e.target.value
                          ? e.target.value.split(",").map((s) => s.trim())
                          : [],
                      )
                    }
                    placeholder="Ex: Lomé, Cotonou"
                  />
                  <p className="text-xs text-gray-500">
                    Séparez par des virgules
                  </p>
                </div>
              </>
            )}

            {/* Filtres spécifiques aux prospects */}
            {filterType === "prospects" && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Qualifié
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={
                      filters.is_qualified === ""
                        ? ""
                        : filters.is_qualified === true
                          ? "true"
                          : "false"
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "is_qualified",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">Tous</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Rendez-vous
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={
                      filters.has_rendez_vous === ""
                        ? ""
                        : filters.has_rendez_vous === true
                          ? "true"
                          : "false"
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "has_rendez_vous",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">Tous</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sexe
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.sexe || ""}
                    onChange={(e) => handleInputChange("sexe", e.target.value)}
                  >
                    <option value="">Tous</option>
                    <option value="M">Masculin</option>
                    <option value="F">Féminin</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Secteurs d'activité
                  </label>
                  <Select
                    isMulti
                    options={SECTEUR_OPTIONS_PROSPECT}
                    value={SECTEUR_OPTIONS_PROSPECT.filter((o) =>
                      (filters.secteurs_activite || []).includes(o.value),
                    )}
                    onChange={(selected) =>
                      handleInputChange(
                        "secteurs_activite",
                        selected ? selected.map((s) => s.value) : [],
                      )
                    }
                    placeholder="Sélectionner..."
                    noOptionsMessage={() => "Aucun résultat"}
                    styles={reactSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Profession
                  </label>
                  <input
                    type="text"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.professions?.join(", ") || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "professions",
                        e.target.value
                          ? e.target.value.split(",").map((s) => s.trim())
                          : [],
                      )
                    }
                    placeholder="Ex: Directeur, Manager"
                  />
                  <p className="text-xs text-gray-500">
                    Séparez par des virgules
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Nationalités
                  </label>
                  <Select
                    isMulti
                    options={countries}
                    value={countries.filter((c) =>
                      (filters.nationalites || []).includes(c.value),
                    )}
                    onChange={(selected) =>
                      handleInputChange(
                        "nationalites",
                        selected ? selected.map((s) => s.value) : [],
                      )
                    }
                    placeholder="Sélectionner..."
                    noOptionsMessage={() => "Aucun résultat"}
                    styles={reactSelectStyles}
                    menuPortalTarget={
                      typeof document !== "undefined" ? document.body : null
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Est professionnel
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={
                      filters.is_professionnel === ""
                        ? ""
                        : filters.is_professionnel === true
                          ? "true"
                          : "false"
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "is_professionnel",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">Tous</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Est actif
                  </label>
                  <select
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={
                      filters.is_active === ""
                        ? ""
                        : filters.is_active === true
                          ? "true"
                          : "false"
                    }
                    onChange={(e) =>
                      handleInputChange(
                        "is_active",
                        e.target.value === "" ? "" : e.target.value === "true",
                      )
                    }
                  >
                    <option value="">Tous</option>
                    <option value="true">Oui</option>
                    <option value="false">Non</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Date rendez-vous début
                  </label>
                  <input
                    type="date"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.date_rendez_vous_start || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "date_rendez_vous_start",
                        e.target.value,
                      )
                    }
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Date rendez-vous fin
                  </label>
                  <input
                    type="date"
                    className="block w-full p-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-amber-500 focus:border-amber-500"
                    value={filters.date_rendez_vous_end || ""}
                    onChange={(e) =>
                      handleInputChange("date_rendez_vous_end", e.target.value)
                    }
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-between gap-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:text-sm"
            >
              Réinitialiser
            </button>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-green-300 shadow-sm px-4 py-2 bg-green-50 text-base font-medium text-green-700 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 sm:text-sm"
            >
              {loading ? "Export..." : "Exporter Excel"}
            </button>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 sm:text-sm"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-amber-500 text-base font-medium text-white hover:bg-amber-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 sm:text-sm"
            >
              {loading ? "Application..." : "Appliquer les filtres"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
